import { Middleware } from '@reduxjs/toolkit';
import { initializeSocket, getSocket } from '../../socket';
import { updateTask, deleteTask, createTask, updateTaskMiniTask, deleteTaskMiniTask, createTaskMiniTask } from '../slices/tasksSlice';
import { setToken, clearToken, setSocketInitialized, setReconnectionCountdown  } from '../slices/authSlice';
import { createEvent, deleteEvent, updateEvent } from '../slices/eventsSlice';
import { createRecurringTask, deleteRecurringTask, updateRecurringTask } from '../slices/recurringTaskSlice';
import { RootState } from '../store';
import { setSelectedProject } from '../slices/projectsSlice';
import { setSocketConnected } from '../slices/authSlice';
import api from '../../axios';

const selectSelectedProjectId = (state: RootState) => state.projects.selectedProjectId;

let socketInitialized: boolean = false;
let currentlyRotatingAuth: boolean = false;

/* Socket connection flow
 * 1. Try to connect with current auth token
 * 2. If fails, catch in 'connect_error'
 * 3. If unauthorized, make an axios request to /ping
 * 4. Axios should catch the 401 and request for the generation of a new token
 * 5. Axios interceptor will set new token state, calling the socketMiddleware function again
 * 6. Reconnec with new access token
 */

export const socketMiddleware: Middleware = store => next => action => {
  // if the action is setToken
  if (setToken.match(action)) {
    const authToken = action.payload; // fetch token

    // if the socket not yet initialized
    if (!socketInitialized) {
      const socket = initializeSocket(authToken);
      socketInitialized = true;
      
      // socketio events
      socket.on('connect', () => {
        console.log('socket connected:', socket.id);
        currentlyRotatingAuth = false;
        store.dispatch(setSocketConnected(true));
        store.dispatch(setSocketInitialized(true));
      });

      socket.on('disconnect', () => {
        console.log('socket disconnected');
        if (!currentlyRotatingAuth) {
          store.dispatch(setSocketConnected(false));
        }
      });

      socket.io.on('reconnect', () => {
        console.log('socket reconnected!');
        currentlyRotatingAuth = false;
      });

      socket.io.on('reconnect_failed', async () => {
        console.log('socket reconnect failed!');

        let counter = 10;

        for (let i = 1; i <= 10; i += 1) {
          setTimeout(() => {
            counter -= 1;
            store.dispatch(setReconnectionCountdown(counter));
          }, i*1000);
        }

        setTimeout(() => {
          // attempt to connect after 10 seconds
          // if it fails again after 10 attempts, wait 10 seconds etc
          socket.io.connect();
          setReconnectionCountdown(0);
        }, 10*1000);
      });

      socket.io.on('reconnect_error', (err) => {
        store.dispatch(setSocketConnected(false));
      });

      // emitted by server before socket is disconnected due to auth token expiry
      socket.on('auth_expired', () => {
        console.log('socket auth expired');
        // dont set redux socket disconnect to prevent reload
        currentlyRotatingAuth = true;

        // ping the api to check auth, if unauthorized, axios interceptors with fetch a new access token
        api
          .get('ping')
          .catch(console.error);
      });

      socket.on('connect_error', (err) => {
        console.log('socket connection error', err);

        if (err.message === 'Unauthorized') {
          socket.disconnect();
          store.dispatch(setSocketConnected(false));

          // ping the api to check auth, if unauthorized, axios interceptors with fetch a new access token
          api
            .get('ping')
            .catch(console.error);
        } else {
          store.dispatch(setSocketConnected(false));
        }
      });

      // tasks
      socket.on('task.updated', (updateData) => {
        const state = store.getState();
        const selectedProjectId: number | null = selectSelectedProjectId(state);
        
        if (updateData.task.projectId !== selectedProjectId) {
          store.dispatch(deleteTask(Number(updateData.task.id)));
        } else {
          store.dispatch(updateTask(updateData.task));
        }
      });

      socket.on('task.deleted', (taskId) => {
        store.dispatch(deleteTask(Number(taskId.taskId)));
      });

      socket.on('task.created', (createdData) => {
        const state = store.getState();
        const selectedProjectId: number | null = selectSelectedProjectId(state);

        if (createdData.task.projectId !== selectedProjectId) {
          store.dispatch(setSelectedProject(createdData.task.projectId));
        } else {
          store.dispatch(createTask(createdData.task));
        }
      });

      // mini tasks
      socket.on('miniTask.updated', (updateData) => {
        store.dispatch(updateTaskMiniTask(updateData.miniTask));
      });

      socket.on('miniTask.deleted', (miniTaskId) => {
        store.dispatch(deleteTaskMiniTask(Number(miniTaskId.miniTaskId)));
      });

      socket.on('miniTask.created', (createdData) => {
        store.dispatch(createTaskMiniTask(createdData.miniTask));
      });

      // events
      socket.on('event.updated', (updateData) => {
        const state = store.getState();
        const selectedProjectId: number | null = selectSelectedProjectId(state);
        
        if (updateData.event.projectId !== selectedProjectId) {
          store.dispatch(deleteEvent(Number(updateData.event.id)));
        } else {
          store.dispatch(updateEvent(updateData.event));
        }
      });

      socket.on('event.deleted', (eventId) => {
        store.dispatch(deleteEvent(Number(eventId.eventId)));
      });

      socket.on('event.created', (createdData) => {
        const state = store.getState();
        const selectedProjectId: number | null = selectSelectedProjectId(state);

        if (createdData.event.projectId !== selectedProjectId) {
          store.dispatch(setSelectedProject(createdData.event.projectId));
        } else {
          store.dispatch(createEvent(createdData.event));
        }
      });

      // recurring tasks
      socket.on('recurring.updated', (updateData) => {
        const state = store.getState();
        const selectedProjectId: number | null = selectSelectedProjectId(state);
        
        if (updateData.recurringTask.projectId !== selectedProjectId) {
          store.dispatch(deleteRecurringTask(Number(updateData.recurringTask.id)));
        } else {
          store.dispatch(updateRecurringTask(updateData.recurringTask));
        }
      });

      socket.on('recurring.deleted', (deletedData) => {
        store.dispatch(deleteRecurringTask(Number(deletedData.recurringTaskId)));
      });

      socket.on('recurring.created', (createdData) => {
        const state = store.getState();
        const selectedProjectId: number | null = selectSelectedProjectId(state);

        if (createdData.recurringTask.projectId !== selectedProjectId) {
          store.dispatch(setSelectedProject(createdData.recurringTask.projectId));
        } else {
          store.dispatch(createRecurringTask(createdData.recurringTask));
        }
      });

    } else {
      // if the socket is initialized but the authToken has been updated
      try {
        const socket = getSocket();
        // socket.disconnect();
        socket.auth = (cb) => cb({ token: authToken });
        socket.connect(); // triggers re-authentication
      } catch (e) {
        console.error('Socket not initialized but tried to update token.');
      }
    }
  }

  if (clearToken.match(action) && socketInitialized) {
    try {
      const socket = getSocket();
      socket.disconnect();
    } catch (e) {
      console.error('Socket not initialized during disconnect.');
    }
    socketInitialized = false;
  }

  return next(action);
};